//dependencies
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React, { useEffect, useRef, useState } from "react";
import ReactTextareaAutosize from "react-textarea-autosize";
import { useOnClickOutside } from "usehooks-ts";

import { faCheckCircle, faCircleXmark, faGripLines, faPencil } from "@fortawesome/pro-solid-svg-icons";

//components
import Icon from "../../../../../../../components/Icon/Icon";
import InputCheckbox from "../../../../../../../components/Inputs/Checkbox";
import Spinner from "../../../../../../../components/Spinner";
import Typography from "../../../../../../../components/Text";

//css
import css from "../../Checklist.module.scss";

export type ChecklistItem = {
	_id?: string;
	text?: string;
	checked?: boolean;
	pending?: boolean;
	// id used for dnd kit
	id?: string;
};

type ChecklistItemProps = {
	item: ChecklistItem | null;
	onChange: (_item: ChecklistItem, shouldAddItemAfter?: boolean) => void;
	onDelete: (_itemId: string) => void;
	overrideId?: string;
	isDraggable?: boolean;
	isEditable?: boolean;
	isDeletable?: boolean;
	isCreateNew?: boolean;
	isLoading?: boolean;
};

const ChecklistItem = ({
	item,
	onDelete,
	onChange,
	overrideId = "",
	isDraggable = false,
	isEditable = false,
	isDeletable = false,
	isCreateNew = false,
	isLoading = false,
}: ChecklistItemProps) => {
	const [editEnabled, setEditEnabled] = useState(false);
	const [checklistInput, setChecklistInput] = useState(item?.text || "");
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const { attributes, listeners, setNodeRef, transform, transition, setActivatorNodeRef } = useSortable({
		id: item?._id || "",
	});

	const style = {
		transform: CSS.Translate.toString(transform),
		transition,
	};

	if (checklistInput === "" && !editEnabled) {
		setEditEnabled(true);
	}

	const handleEdit = () => {
		// when adding a new checklist item, reset the add checklist input
		if (isCreateNew && checklistInput !== "") {
			//since the component is controlled, we need to get the ref and set the value to empty
			if (inputRef.current) {
				inputRef.current.value = "";
			}
			setChecklistInput("");
		} else if (checklistInput === "") {
			return;
		}
		setEditEnabled(false);
		onChange({ checked: false, ...item, text: checklistInput });
	};

	const handleEditClick = () => {
		if (!editEnabled) {
			setEditEnabled(true);
			return;
		} else {
			handleEdit();
			return;
		}
	};

	const handleCheckBoxClick = () => {
		onChange({ ...item, checked: !item?.checked });
	};

	useOnClickOutside(containerRef, () => {
		if (editEnabled) {
			handleEdit();
		}
	});

	const id = overrideId || item?._id;

	useEffect(() => {
		if (editEnabled) {
			inputRef.current?.focus();
			//highlight the text in the input
			inputRef.current?.select();
		}
	}, [editEnabled]);

	const buttons = (
		<div className={css.buttonsContainer}>
			{!editEnabled && isEditable && (
				<button
					data-testid={`${item?._id}-edit-button-checklist-item`}
					disabled={!isEditable}
					onClick={handleEditClick}
					className={`${css.iconButton} cursor-pointer disabled:cursor-not-allowed`}
				>
					<Icon icon={faPencil} size="small" />
				</button>
			)}
			{editEnabled && isEditable && (
				<button
					data-testid={`${item?._id}-confirm-button-checklist-item`}
					disabled={!isEditable || !checklistInput}
					className={`${css.addButton} cursor-pointer disabled:cursor-not-allowed`}
					onClick={handleEditClick}
					type="submit"
				>
					<Icon icon={faCheckCircle} size="small" />
				</button>
			)}
			{isDeletable && (
				<button
					data-testid={`${item?._id}-delete-or-cancel-button-checklist-item`}
					disabled={!isDeletable}
					onClick={() => {
						if (editEnabled) {
							setEditEnabled(false);
							setChecklistInput(item?.text || "");
						} else if (item?._id) {
							onDelete(item._id);
						}
					}}
					className={`${editEnabled ? css.removeButton : css.iconButton} cursor-pointer disabled:cursor-not-allowed`}
				>
					<Icon icon={faCircleXmark} size="small" />
				</button>
			)}
		</div>
	);

	const createButtons = (
		<div className={css.createButtonsContainer}>
			<button
				data-testid={`create-edit-button-checklist-item`}
				disabled={!isDeletable}
				onClick={handleEdit}
				className={`${css.addButton} cursor-pointer disabled:cursor-not-allowed`}
			>
				<Icon icon={faCheckCircle} size="small" />
			</button>

			{isDeletable && (
				<button
					data-testid={`cancel-create-button-checklist-item`}
					disabled={!isDeletable}
					onClick={() => onDelete("")}
					className={`${css.removeButton} cursor-pointer disabled:cursor-not-allowed`}
				>
					<Icon icon={faCircleXmark} size="small" />
				</button>
			)}
		</div>
	);

	const handleSubmit = (event: React.FormEvent<HTMLFormElement> | React.KeyboardEvent<HTMLTextAreaElement>) => {
		event.preventDefault();
		handleEdit();
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		// Enter-only submits, shift+enter is ignored
		if (event.key === "Enter") {
			event.preventDefault();
			if (!event.shiftKey && checklistInput) {
				handleSubmit(event);
			}
		}
	};

	// Parses text entered in checklist input and strips line break characters
	const parseChecklistText = (text: string) => {
		const parsedText = text.replace(/(\r\n|\n|\r)/gm, "");

		return parsedText;
	};

	const handleChange = (text: string) => {
		const parsedText = parseChecklistText(text);
		setChecklistInput(parsedText);

		if (inputRef?.current) {
			inputRef.current.value = parsedText;
		}
	};

	return (
		<div ref={setNodeRef} style={style} {...attributes} data-testid={item?._id}>
			<div className={css.checklistItem} ref={containerRef}>
				<div
					ref={setActivatorNodeRef}
					id={`activator-${item?._id}`}
					className={`${css.gripIcon} ${!item?._id || !isDraggable ? css.disableDrag : ""}`}
					{...listeners}
				>
					<Icon icon={faGripLines} size="small" testid={faGripLines?.iconName} />
				</div>
				<div className={`${css.mainContentContainer} ${editEnabled ? css.editEnabled : ""}`}>
					<form onSubmit={handleSubmit} className={css.inputContainer}>
						{isLoading ? (
							<Spinner centered size={18} />
						) : (
							<InputCheckbox value={item?.checked} onChange={handleCheckBoxClick} disabled={!item?._id} />
						)}
						{editEnabled ? (
							<ReactTextareaAutosize
								id={`${id}-input`}
								data-testid={`${id}-input`}
								className={`${css.input} ${editEnabled ? css.editEnabled : ""}`}
								defaultValue={id === "new-item" ? "" : checklistInput}
								disabled={!editEnabled}
								onChange={(e) => {
									handleChange(e.target.value);
								}}
								ref={inputRef}
								placeholder="Your goal checklist title goes here"
								minRows={1}
								onKeyDown={handleKeyDown}
								onPaste={(e) => {
									//format pasted text to remove line breaks
									e.preventDefault();
									const text = checklistInput + e.clipboardData.getData("text/plain");
									handleChange(text);
								}}
							/>
						) : (
							<Typography size="paragraph-3" weight="regular" className={css.inputText}>
								{item?.text}{" "}
							</Typography>
						)}
					</form>
					{!isCreateNew && buttons}
				</div>
				{isCreateNew && createButtons}
			</div>
		</div>
	);
};

export default ChecklistItem;
